import { Box, FormikSelectInputNative, FormLabel, FormikTextInput, Button } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useEffect } from 'react';

import { Option } from './AvlFormUtils';
import { TwelveMonthsNumberInput } from './TwelveMonthsNumberInput';

interface SolarArrayProps {
  isLoading: boolean;
  arrayNumber: number;
  fieldName: string;
  inverterOptions: Option[];
}

export const SolarArray = ({ isLoading, arrayNumber, fieldName, inverterOptions }: SolarArrayProps) => {
  const { isSubmitting, values, setFieldValue } = useFormikContext<any>();
  const isAnythingLoadingOrSubmitting = isLoading || isSubmitting;

  const removeArray = useCallback(() => {
    setFieldValue(
      'arrays',
      values.arrays.filter((_: any, index: number) => index !== arrayNumber),
    );
  }, [arrayNumber, setFieldValue, values.arrays]);

  const setNumericValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.endsWith('.')) {
        setFieldValue(event.target.name, event.target.value);
      } else {
        const numericValue = Number(event.target.value);
        if (!isNaN(numericValue)) {
          setFieldValue(event.target.name, numericValue);
        }
      }
    },
    [setFieldValue],
  );

  const inverterFieldName = `${fieldName}[${arrayNumber}].inverter`;
  const currentInverterValue = values.arrays?.[arrayNumber]?.inverter;

  useEffect(() => {
    if (inverterOptions?.length === 1 && !currentInverterValue) {
      setFieldValue(inverterFieldName, inverterOptions[0].value);
    }
  }, [inverterOptions, currentInverterValue, inverterFieldName, setFieldValue]);
  return (
    <Box childGap="lg" borderColor="separator" borderWidth="xs" radius="sm" padding="lg">
      <Box direction="row" gap="xs" alignItems="center">
        <Box fontWeight="medium">{`Array ${arrayNumber + 1}`}</Box>
        {arrayNumber > 0 ? (
          <Button
            variant="tertiary"
            tone="neutral"
            style={{ textDecoration: 'underline' }}
            size="xs"
            onClick={removeArray}
          >
            remove
          </Button>
        ) : (
          ''
        )}
      </Box>
      <Box direction={{ base: 'column', tablet: 'row' }} childGap={{ base: 'lg', desktop: 'xl' }}>
        <Field
          label="Panel Count"
          name={`${fieldName}[${arrayNumber}].panelCount`}
          id={`panelCount-${arrayNumber}`}
          component={FormikTextInput}
          isLoading={isAnythingLoadingOrSubmitting}
          maxWidth="25"
          onChange={setNumericValue}
        />
        <Field
          label="Annual TSRF"
          name={`${fieldName}[${arrayNumber}].annualTSRF`}
          id={`panelCount-${arrayNumber}`}
          component={FormikTextInput}
          isLoading={isAnythingLoadingOrSubmitting}
          suffix="%"
          maxWidth="25"
          onChange={setNumericValue}
        />
        <Field
          label="Inverter"
          name={`${fieldName}[${arrayNumber}].inverter`}
          id={`panelCount-${arrayNumber}`}
          component={FormikSelectInputNative}
          options={inverterOptions}
          isLoading={isAnythingLoadingOrSubmitting}
          maxWidth="40"
        />
      </Box>
      <Box direction={{ base: 'column', tablet: 'row' }} childGap={{ base: 'lg', desktop: 'xl' }}>
        <Field
          label="Tilt (Degrees)"
          name={`${fieldName}[${arrayNumber}].tilt`}
          id={`tilt-${arrayNumber}`}
          component={FormikTextInput}
          isLoading={isAnythingLoadingOrSubmitting}
          suffix="°"
          maxWidth="50"
          onChange={setNumericValue}
        />
        <Field
          label="Azimuth (Degrees)"
          name={`${fieldName}[${arrayNumber}].azimuth`}
          id={`azimuth-${arrayNumber}`}
          component={FormikTextInput}
          isLoading={isAnythingLoadingOrSubmitting}
          suffix="°"
          maxWidth="50"
          onChange={setNumericValue}
        />
      </Box>
      <Box childGap={{ base: 'lg', desktop: 'xl' }}>
        <FormLabel inputId={''}>Monthly Solar Access (%)</FormLabel>
        <TwelveMonthsNumberInput
          fieldName={`${fieldName}[${arrayNumber}].monthlySolarAccess`}
          isLoading={isAnythingLoadingOrSubmitting}
          min={0}
          max={100}
        />
      </Box>
    </Box>
  );
};
